import React, { useState } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { getSerializedNotificationInputs } from "./serialization";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  notificationNodeSchema,
  type NotificationNodeData,
} from "@/schemas/workflow/notification-node";
import { getDeserializedNotificationNode } from "./serialization";
import { NotificationForm } from "./components/notification-form";
import Variables from "@/workflow_api/components/variables";
import Empty from "@/workflow_api/components/variables/empty";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

export interface NotificationDetailsProps {
  nodeData: NodeData;
  initialTitle?: string;
}

export const NotificationDetailsContent: React.FC<NotificationDetailsProps> = ({
  initialTitle = "Notificación",
  nodeData,
}) => {
  const [title, setTitle] = useState<string>(initialTitle);
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  const {
    nodeDetailsOpen,
    selectedNodeId,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();

  const { getNameValidationError, isNameValid } = useNodeNameValidation(selectedNodeId);
  const { flowId, getNodeById } = useWorkflowStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const { mutateAsync: updateNode, isPending: isLoadingUpdate } =
    useUpdateNode(flowId);

  const deserializedData = getDeserializedNotificationNode(nodeData?.inputs);
  const defaultValues: NotificationNodeData = deserializedData || {
    template: "no-template",
    title: "",
    description: "",
    emails: [{ type: "inp" as const, value: "" }],
  };

  const form = useForm<NotificationNodeData>({
    resolver: zodResolver(notificationNodeSchema),
    defaultValues,
  });

  const onSave = async () => {
    if (!selectedNodeId) return;
    if (!selectedNode) return;

    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();
    const serializedInputs = getSerializedNotificationInputs(formData);

    await updateNode({
      node_id: selectedNodeId,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: selectedNode.position.x,
        position_y: selectedNode.position.y,
        type: nodeData.node_type,
        manual_continue: manualConfirmation,
        inputs: serializedInputs,
      },
    });
    setNodeDetailsOpen(false);
    setSelectedNodeId();
  };

  const isSaveValid = form.formState.isValid;

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={isLoadingUpdate}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          onSave();
        }}
        isSaveValid={isSaveValid && isNameValid(title)}
        isLoading={isLoadingUpdate}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "bells",
            title: title,
            color: "info",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContentWidth="min-w-[445px]"
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
              <Variables sourceNodeId={selectedNodeId!} />
            </div>
          }
          centerContent={
            <FormProvider {...form}>
              <NotificationForm />
            </FormProvider>
          }
          rightContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)] h-full">
              <Empty />
            </div>
          }
        />
      </FullModal>
    </>
  );
};

const NotificationDetails = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;
  return (
    <NotificationDetailsContent
      nodeData={nodeData}
      initialTitle={nodeData?.displayName}
    />
  );
};

export default NotificationDetails;
